// OrderService.ts
import { SupabaseClient } from "@supabase/supabase-js";
import { getSupabaseClient } from "@shared/core/services/SupabaseService";

class OrderService {
  private tableName: string;
  private supabase: SupabaseClient;

  constructor() {
    this.tableName = "orders";
    this.supabase = getSupabaseClient();
  }
  async getAllOrders(page = 1, pageSize = 10, websiteId = null) {
    try {
      // Validate websiteId
      if (!websiteId) {
        throw new Error("Website ID is required to fetch orders");
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Add error handling for the main query
      let query = this.supabase.from(this.tableName).select(
        `
          id,
          order_reference,
          customer_email,
          shipping_address,
          shipping_option,
          total_amount,
          vat_amount,
          currency,
          status,
          created_at,
          updated_at,
          website_id,
          order_items!inner (
            id,
            order_id,
            quantity,
            products!inner (
              id,
              name
            )
          )
        `,
        { count: "exact" },
      );

      // Filter by website_id
      query = query.eq("website_id", websiteId);

      // Order by date and apply range
      query = query.order("created_at", { ascending: false }).range(from, to);

      const { data, error, count } = await query;

      // Detailed error logging
      if (error) {
        console.error("Supabase query error:", {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
        throw error;
      }

      // Return data as-is - decryption happens server-side via API
      const decryptedData = data || [];

      return { data: decryptedData, count };
    } catch (error) {
      // Enhanced error logging
      console.error("Error in getAllOrders:", {
        error,
        message: error.message,
        code: error.code,
        details: error?.details,
        stack: error.stack,
      });
      throw error;
    }
  }

  async getOrderById(id, websiteId = null) {
    try {
      let query = this.supabase
        .from(this.tableName)
        .select(
          `
          id,
          order_reference,
          customer_email_encrypted,
          shipping_address_encrypted,
          shipping_option,
          total_amount,
          vat_amount,
          currency,
          status,
          created_at,
          updated_at,
          website_id,
          stripe_payment_intent,
          tracking_number,
          tracking_carrier,
          order_notes,
          order_items (
            id,
            order_id,
            product_id,
            quantity,
            price_at_time,
            created_at,
            products (
              id,
              name,
              description,
              price,
              image
            )
          )
        `,
        )
        .eq("id", id);

      // If websiteId provided, ensure order belongs to this website
      if (websiteId) {
        query = query.eq("website_id", websiteId);
      }

      const { data, error } = await query.single();

      if (error) {
        console.error("Error fetching order by ID:", {
          orderId: id,
          websiteId,
          error: error,
        });
        throw error;
      }

      // Return data as-is - decryption happens server-side via API
      const decryptedOrder = data;

      return decryptedOrder;
    } catch (error) {
      console.error("Error in getOrderById:", {
        orderId: id,
        error: error,
      });
      throw error;
    }
  }

  async updateOrderStatus(id, status) {
    try {
      const { data, error } = await this.supabase
        .from(this.tableName)
        .update({ status })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating order status:", {
          orderId: id,
          status,
          error: error,
        });
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error in updateOrderStatus:", {
        orderId: id,
        status,
        error: error,
      });
      throw error;
    }
  }

  async updateOrder(id, updates) {
    try {
      const { data, error } = await this.supabase
        .from(this.tableName)
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select(
          `
          *,
          order_items (
            id,
            order_id,
            product_id,
            quantity,
            price_at_time,
            products (
              id,
              name,
              stock_quantity
            )
          )
        `,
        )
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error in updateOrder:", error);
      throw error;
    }
  }

  async updateTrackingInfo(id, trackingNumber, trackingCarrier = null) {
    return this.updateOrder(id, {
      tracking_number: trackingNumber,
      tracking_carrier: trackingCarrier,
      // If adding tracking, auto-update status to shipped if it was processing
      ...(trackingNumber && { status: "shipped" }),
    });
  }

  async addOrderNote(id, note) {
    try {
      // First get existing notes
      const { data: order } = await this.getOrderById(id);
      const existingNotes = order.order_notes || [];

      const newNote = {
        text: note,
        created_at: new Date().toISOString(),
        created_by: "admin", // You might want to get actual user info
      };

      return this.updateOrder(id, {
        order_notes: [...existingNotes, newNote],
      });
    } catch (error) {
      console.error("Error adding order note:", error);
      throw error;
    }
  }

  async cancelOrder(id) {
    try {
      // Get the full order details first
      const { data: order, error: fetchError } = await this.supabase
        .from(this.tableName)
        .select(
          `
          *,
          order_items (
            id,
            product_id,
            quantity,
            products (
              id,
              stock_quantity
            )
          )
        `,
        )
        .eq("id", id)
        .single();

      if (fetchError) {
        console.error("Error fetching order for cancellation:", fetchError);
        throw new Error("Order not found");
      }

      // Check if order can be cancelled
      if (["shipped", "delivered", "cancelled"].includes(order.status)) {
        throw new Error(`Cannot cancel order with status: ${order.status}`);
      }

      // Restore stock for each item
      for (const item of order.order_items) {
        const newStockQuantity =
          (item.products.stock_quantity || 0) + item.quantity;

        const { error: stockError } = await this.supabase
          .from("products")
          .update({
            stock_quantity: newStockQuantity,
            in_stock: true, // Ensure product is marked as in stock
            updated_at: new Date().toISOString(),
          })
          .eq("id", item.product_id);

        if (stockError) {
          console.error(
            "Error restoring stock for product:",
            item.product_id,
            stockError,
          );
          throw stockError;
        }
      }

      // Update order status
      return this.updateOrderStatus(id, "cancelled");
    } catch (error) {
      console.error("Error in cancelOrder:", error);
      throw error;
    }
  }

  // Add validation for status transitions
  isValidStatusTransition(currentStatus, newStatus) {
    const validTransitions = {
      paid: ["processing", "cancelled"],
      processing: ["shipped", "cancelled"],
      shipped: ["delivered"],
      delivered: [],
      cancelled: [],
    };

    return validTransitions[currentStatus]?.includes(newStatus) || false;
  }

  getNextValidStatus(currentStatus) {
    const validTransitions = {
      paid: "processing",
      processing: "shipped",
      shipped: "delivered",
      delivered: null,
      cancelled: null,
    };

    return validTransitions[currentStatus];
  }

  /**
   * Get decrypted customer data for an order (admin only)
   * Calls the krystal-api decryption endpoint
   */
  async getDecryptedOrderData(orderId, websiteId = null) {
    try {
      // Get session for authentication
      const {
        data: { session },
      } = await this.supabase.auth.getSession();

      if (!session) {
        throw new Error("Authentication required for order decryption");
      }

      // Use websiteId from parameters or try to get from context
      const targetWebsiteId = websiteId || this.getWebsiteIdFromContext();
      if (!targetWebsiteId) {
        throw new Error("Website ID is required for order decryption");
      }

      // Call the decryption API
      const decryptionApiUrl = `${import.meta.env.VITE_KRYSTAL_API_URL || "https://krystal-api.nyesservices.co.uk"}/api/privacy/decrypt-order`;

      const response = await fetch(decryptionApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          order_id: orderId,
          website_id: targetWebsiteId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Decryption API error: ${errorData.error || response.statusText}`,
        );
      }

      const result = await response.json();

      return result.decrypted_data;
    } catch (error) {
      console.error("Error getting decrypted order data:", {
        orderId,
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Get decrypted customer data for multiple orders (batch operation)
   * Efficient for admin pages that display many orders
   */
  async getDecryptedOrderDataBatch(orderIds, websiteId = null) {
    try {
      // Get session for authentication
      const {
        data: { session },
      } = await this.supabase.auth.getSession();

      if (!session) {
        throw new Error("Authentication required for batch order decryption");
      }

      // Use websiteId from parameters or try to get from context
      const targetWebsiteId = websiteId || this.getWebsiteIdFromContext();
      if (!targetWebsiteId) {
        throw new Error("Website ID is required for batch order decryption");
      }

      // Validate order IDs array
      if (!Array.isArray(orderIds) || orderIds.length === 0) {
        throw new Error("Order IDs array is required for batch decryption");
      }

      if (orderIds.length > 50) {
        throw new Error("Maximum 50 orders can be decrypted in a single batch");
      }

      // Call the batch decryption API
      const decryptionApiUrl = `${import.meta.env.VITE_KRYSTAL_API_URL || "https://krystal-api.nyesservices.co.uk"}/api/privacy/decrypt-orders-batch`;

      const response = await fetch(decryptionApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          order_ids: orderIds,
          website_id: targetWebsiteId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Batch decryption API error: ${errorData.error || response.statusText}`,
        );
      }

      const result = await response.json();

      return result.decrypted_orders;
    } catch (error) {
      console.error("Error getting batch decrypted order data:", {
        orderIdsCount: orderIds?.length,
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Enhanced order fetching with decrypted customer data
   * Uses the regular getOrderById and then decrypts sensitive fields
   */
  async getOrderWithDecryptedData(id, websiteId = null) {
    try {
      // Get the basic order data
      const order = await this.getOrderById(id, websiteId);

      // Get decrypted customer data
      try {
        const decryptedData = await this.getDecryptedOrderData(id, websiteId);

        // Merge decrypted data with order data
        return {
          ...order,
          decrypted_customer_email: decryptedData.customer_email,
          decrypted_shipping_address: decryptedData.shipping_address,
          has_encrypted_data: true,
        };
      } catch (decryptionError) {
        console.warn("Failed to decrypt order data, using existing data:", {
          orderId: id,
          error: decryptionError.message,
        });

        // Return order with fallback data
        return {
          ...order,
          decrypted_customer_email: null,
          decrypted_shipping_address: null,
          has_encrypted_data: false,
          decryption_error: decryptionError.message,
        };
      }
    } catch (error) {
      console.error("Error in getOrderWithDecryptedData:", {
        orderId: id,
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Helper method to get website ID from context/environment
   * This should be enhanced based on your app's context management
   */
  private getWebsiteIdFromContext() {
    // Try to get from environment variables first (browser/Vite)
    try {
      if (typeof window !== "undefined" && import.meta?.env?.VITE_WEBSITE_ID) {
        return import.meta.env.VITE_WEBSITE_ID;
      }
    } catch {
      // import.meta might not be available in all contexts
    }

    // Try to get from process environment (Node.js)
    if (typeof process !== "undefined" && process.env?.VITE_WEBSITE_ID) {
      return process.env.VITE_WEBSITE_ID;
    }

    // Could be enhanced to get from React context, URL params, etc.
    console.warn(
      "Website ID not found in context, will require explicit parameter",
    );
    return null;
  }
}

export const orderService = new OrderService();
